import { EnvironmentInjector, Injectable, runInInjectionContext } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { UserSign } from 'app/auth/models/user.model';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Servicio que maneja todas las conexiones con la BD firebase
 */
@Injectable({
  providedIn: 'root',
})
export class DbService {
  /**
   * Informacion del usuario logueado
   */
  user: UserSign = {} as UserSign;

  /**
   * Constructor
   *
   * @param db conexcion con la bd
   * @param injector contexto de inyección, necesario para envolver las llamadas
   * al SDK compat de Firestore (ver nota en runInInjectionContext más abajo)
   */
  constructor(
    private db: AngularFirestore,
    private injector: EnvironmentInjector,
  ) {

  }

  /**
   * Agrega un registro en una coleccion sobreescribiendo
   *
   * @param collection nombre de la coleccion
   * @param object objeto a agregar
   */
  set(collection: string, object: any): any {
    return runInInjectionContext(this.injector, () => this.db.doc(collection).set(object));
  }

  /**
   * Agrega un registro en una coleccion
   *
   * @param collection nombre de la coleccion
   * @param object objeto a agregar
   */
  add(collection: string, object: any): any {
    const date = new Date();
    const dateNow = moment(date).format('DD/MM/YYYY hh:mm a');
    object.create = dateNow;
    object.createAt = date;
    return runInInjectionContext(this.injector, () => this.db.collection(collection).add(object));
  }
  /**
   * Lista los registros de una coleccion
   *
   * @param collection nombre de la coleccion a listar
   */
  get(collection: string, queryfn?: any[]): Observable<any> {
    return runInInjectionContext(this.injector, () => this.db.collection(collection, (ref: any) => {
      if (queryfn) {
        if (!Array.isArray(queryfn[0])) {
          const [path, op, value] = queryfn;
          ref = ref.where(path, op, value);
        } else {
          queryfn.forEach(condition => {
            const [path, op, value] = condition;
            ref = ref.where(path, op, value);
          });
        }
      };
      return ref;
    }).snapshotChanges()).pipe(
      map((action: any) => action.map((a: any) => {
        const data = a.payload.doc.data();
        const id = a.payload.doc.id;
        // eslint-disable-next-line @typescript-eslint/ban-types
        return { id, ...data as {} };
      }))
    );
  }

  /**
   * Obtiene un registro por id
   *
   * @param collection nombre de la coleccion
   * @param id identificador del registro
   */
  findOne(collection: string, id: string): Observable<any> {
    return runInInjectionContext(this.injector, () => this.db.collection(collection).doc(id).valueChanges());
  }

  /**
   * Actualiza un registro en una coleccion
   *
   * @param colection nombre de la coleccion
   * @param id id de registro a actualizar
   * @param object objeto a actualizar
   */
  update(collection: string, id: string, object: any): any {
    const date = new Date();
    const dateNow = moment(date).format('DD/MM/YYYY hh:mm a');
    object.update = dateNow;
    return runInInjectionContext(this.injector, () => this.db.doc(collection + '/' + id).update(object));
  }


  /**
   * Indica si un valor ya existe en una coleccion en especifico
   *
   * @param collection nombre de la coleccion a consultar
   * @param where la propiedad que se va a consultar
   * @param filterValue el valor que se quiere validar
   * @returns indica si ya existe o no
   */
  exist(collection: string, where: string, filterValue: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.get(collection, [where, '==', filterValue]).subscribe((s: any[]) => {
        if (s.length > 0) {
          reject(false);
        } else {
          resolve(true);
        }
      });

    });
  }

  /**
   * Elimina un registro de una coleccion
   *
   * @param id id de registro a eliminar
   */
  delete(collection: string, id: string): Promise<any> {
    return runInInjectionContext(this.injector, () => this.db.doc(collection + '/' + id).delete());
  }
}
